import React from 'react';
import { BaseEdge, EdgeLabelRenderer, EdgeProps, getBezierPath } from 'reactflow';
import { Box, CircularProgress, Tooltip, Stack } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Functions as FunctionsIcon,
  Link as LinkIcon,
  BarChart as BarChartIcon,
  ArrowForward as ArrowForwardIcon,
  Transform as TransformIcon,
  AutoAwesome as AutoAwesomeIcon,
  Edit as EditIcon,
} from '@mui/icons-material';

// Map transformation type to icon
const getTransformationIcon = (transformationType?: string) => {
  const type = (transformationType || '').toLowerCase();
  const iconProps = { sx: { fontSize: 16 } }; // Increased from 12 to 16
  
  if (type.includes('expression') || type.includes('formula')) {
    return <FunctionsIcon {...iconProps} />;
  }
  if (type.includes('lookup') || type.includes('join')) {
    return <LinkIcon {...iconProps} />;
  }
  if (type.includes('aggregat') || type.includes('sum') || type.includes('count')) {
    return <BarChartIcon {...iconProps} />;
  }
  if (type === 'direct' || type === 'passthrough') {
    return <ArrowForwardIcon {...iconProps} />;
  }
  if (type) {
    return <TransformIcon {...iconProps} />;
  }
  return null;
};

/**
 * Phase 1 (P0): Bundle edge between collapsed groups.
 * Shows as a thick semi-transparent line with a label chip indicating "{N} mappings".
 */
export const BundleEdge: React.FC<EdgeProps> = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style = {},
  markerEnd,
  data,
}) => {
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  const mappingCount = data?.bundleMappingCount ?? 0;
  const bundleColor = '#6366f1'; // indigo

  return (
    <>
      {/* Wide invisible hit-area for click targeting */}
      <path
        d={edgePath}
        fill="none"
        stroke="transparent"
        strokeWidth={24}
        style={{ pointerEvents: 'stroke' }}
      />
      <BaseEdge
        path={edgePath}
        markerEnd={markerEnd}
        style={{
          ...style,
          stroke: alpha(bundleColor, 0.35),
          strokeWidth: 4,
          strokeDasharray: '8 4',
        }}
      />
      <EdgeLabelRenderer>
        <Box
          sx={{
            position: 'absolute',
            transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
            pointerEvents: 'all',
            zIndex: 10,
          }}
        >
          <Box
            sx={{
              bgcolor: alpha(bundleColor, 0.1),
              border: `1px solid ${alpha(bundleColor, 0.3)}`,
              borderRadius: 2,
              px: 1,
              py: 0.25,
              fontSize: 11,
              fontWeight: 600,
              color: bundleColor,
              whiteSpace: 'nowrap',
              cursor: 'pointer',
              '&:hover': {
                bgcolor: alpha(bundleColor, 0.2),
                borderColor: alpha(bundleColor, 0.5),
              },
            }}
          >
            {mappingCount} mapping{mappingCount !== 1 ? 's' : ''}
          </Box>
        </Box>
      </EdgeLabelRenderer>
    </>
  );
};

export const ConfidenceEdge: React.FC<EdgeProps> = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style = {},
  markerEnd,
  data,
}) => {
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
    curvature: 0.25,
  });

  const confidence = data?.confidence;
  const transformationType = data?.transformationType || data?.kind || 'direct';

  const transformationSnippet = typeof data?.transformation === 'string' ? data.transformation : undefined;

  const showConfidence = typeof confidence === 'number';
  const TransformIcon = getTransformationIcon(transformationType);

  return (
    <>
      {/* Invisible wider path for easier click targeting */}
      <path
        d={edgePath}
        fill="none"
        stroke="transparent"
        strokeWidth={20}
        style={{ pointerEvents: 'stroke' }}
      />
      <BaseEdge path={edgePath} markerEnd={markerEnd} style={{
        ...style,
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
      }} />
      {(showConfidence || TransformIcon) && (
        <EdgeLabelRenderer>
          <Box
            sx={{
              position: 'absolute',
              // Position label 70% toward target to avoid midpoint clustering
              transform: `translate(-50%, -50%) translate(${labelX + (targetX - labelX) * 0.35}px, ${labelY + (targetY - labelY) * 0.35}px)`,
              pointerEvents: 'all',
              zIndex: 10,
            }}
          >
            <Stack 
              direction="row" 
              spacing={0.5} 
              alignItems="center"
              sx={{ 
                bgcolor: 'background.paper', 
                borderRadius: 2, 
                px: 0.75, 
                py: 0.25,
                boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              {/* AI Generation Indicator */}
              {data?.aiGenerated ? (
                  <Tooltip title="AI Generated" arrow placement="top">
                    <Box sx={{ display: 'flex', color: '#2563eb' }}>
                       <AutoAwesomeIcon sx={{ fontSize: 14 }} />
                    </Box>
                  </Tooltip>
              ) : (
                  <Tooltip title="Manually Created/Edited" arrow placement="top">
                    <Box sx={{ display: 'flex', color: 'text.secondary' }}>
                       <EditIcon sx={{ fontSize: 14 }} />
                    </Box>
                  </Tooltip>
              )}

              {/* Transformation Type Icon */}
              {TransformIcon && (
                <Tooltip 
                  title={transformationSnippet || transformationType || 'Transformation'} 
                  placement="top"
                  arrow
                >
                  <Box 
                    onClick={(e) => {
                      e.stopPropagation();
                      data?.onIconClick?.(id);
                    }}
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 0.5,
                      color: 'primary.main',
                      cursor: 'pointer',
                      bgcolor: '#eff6ff', 
                      borderRadius: 1,
                      pl: 0.5, 
                      pr: data?.label ? 1 : 0.5,
                      py: 0.25,
                      height: 24, 
                      minWidth: 24, 
                      border: '1px solid #bfdbfe',
                      '&:hover': {
                        bgcolor: '#dbeafe',
                        borderColor: '#93c5fd',
                      }
                    }}
                  >
                    {TransformIcon}
                    {data?.label && typeof data.label === 'string' && (
                      <div style={{ fontSize: 10, fontWeight: 600, whiteSpace: 'nowrap' }}>
                         {data.label}
                      </div>
                    )}
                  </Box>
                </Tooltip>
              )}
              
              {/* Confidence Circle */}
              {showConfidence && (
                <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                  <CircularProgress 
                    variant="determinate" 
                    value={confidence * 100} 
                    size={18} 
                    thickness={5} 
                    sx={{ 
                      color: confidence >= 0.9 ? '#10b981' : confidence >= 0.7 ? '#f59e0b' : '#ef4444' 
                    }} 
                  />
                </Box>
              )}
            </Stack>
          </Box>
        </EdgeLabelRenderer>
      )}
    </>
  );
};

